// ================================================================
// InviteAccept — صفحه‌ی تمام‌صفحه‌ی پذیرش دعوت (/invite/:token)
// ================================================================
// عمومی است (نیاز به لاگین ندارد). کاربر اسم/پسورد می‌ذاره، حساب
// ساخته و فعال می‌شه و مستقیم وارد اپ می‌شه.

import './auth-gate.css';
import { t } from '../../i18n/index.js';
import { icon, escapeHtml } from '../../utils/dom.js';
import { fetchInvite, acceptInvite } from '../../core/auth.js';
import { authShell, bindLangSelect, redrawKeepingInput } from './authShell.js';

let rootEl = null;
let token = '';
let invite = null; // { email, organization, role }
let loading = true;
let invalid = false;
let busy = false;
let showPw = false;
let errorMsg = '';

export function showInviteAccept(container, inviteToken) {
  rootEl = container;
  token = inviteToken;
  loading = true;
  invalid = false;
  busy = false;
  errorMsg = '';
  invite = null;
  document.title = t('app.title');
  draw();
  load();
}

async function load() {
  try {
    invite = await fetchInvite(token);
  } catch {
    invalid = true;
  } finally {
    loading = false;
    draw();
  }
}

// Siehe authShell.redrawKeepingInput: ohne das leert jedes Neuzeichnen
// (Fehler, Auge, Sprachwechsel) Name und Passwort der Einladung.
function draw() {
  redrawKeepingInput(rootEl, render);
}

function render() {
  if (loading) {
    rootEl.innerHTML = authShell(`
      <h1 class="auth-heading">${escapeHtml(t('invite.title'))}</h1>
      <p class="auth-subtitle">${escapeHtml(t('invite.loading'))}</p>`);
    bindLangSelect(rootEl, draw);
    return;
  }

  if (invalid) {
    rootEl.innerHTML = authShell(`
      <h1 class="auth-heading">${escapeHtml(t('invite.invalidTitle'))}</h1>
      <p class="auth-subtitle">${escapeHtml(t('invite.invalidText'))}</p>
      <a class="auth-submit" href="/">
        ${icon('arrow_back', 20)}<span>${escapeHtml(t('invite.backToLogin'))}</span>
      </a>`);
    bindLangSelect(rootEl, draw);
    return;
  }

  rootEl.innerHTML = authShell(`
    <h1 class="auth-heading">${escapeHtml(t('invite.title'))}</h1>
    <p class="auth-subtitle">${escapeHtml(t('invite.subtitle', { org: invite.organization }))}</p>

    <form id="inv-form" class="auth-form" novalidate>
      <label class="auth-field">
        <span class="auth-label">${escapeHtml(t('invite.email'))}</span>
        <span class="auth-inputwrap">
          <span class="auth-ico">${icon('mail', 20)}</span>
          <input class="auth-input" type="email" value="${escapeHtml(invite.email)}" disabled />
        </span>
        ${invite.role ? `<span class="auth-hint">${escapeHtml(t('invite.roleLine', { role: invite.role }))}</span>` : ''}
      </label>

      <label class="auth-field">
        <span class="auth-label">${escapeHtml(t('invite.name'))}</span>
        <span class="auth-inputwrap">
          <span class="auth-ico">${icon('person', 20)}</span>
          <input class="auth-input" type="text" name="name" autocomplete="name"
                 placeholder="${escapeHtml(t('auth.namePlaceholder'))}" />
        </span>
      </label>

      <label class="auth-field">
        <span class="auth-label">${escapeHtml(t('invite.password'))}</span>
        <span class="auth-inputwrap">
          <span class="auth-ico">${icon('lock', 20)}</span>
          <input class="auth-input" type="${showPw ? 'text' : 'password'}" name="password" autocomplete="new-password"
                 placeholder="${escapeHtml(t('auth.passwordPlaceholder'))}" />
          <button type="button" class="auth-eye" data-eye aria-label="${escapeHtml(t('auth.togglePassword'))}">
            ${icon(showPw ? 'visibility_off' : 'visibility', 20)}
          </button>
        </span>
        <span class="auth-hint">${escapeHtml(t('auth.passwordHint'))}</span>
      </label>

      <label class="auth-field">
        <span class="auth-label">${escapeHtml(t('invite.confirm'))}</span>
        <span class="auth-inputwrap">
          <span class="auth-ico">${icon('lock', 20)}</span>
          <input class="auth-input" type="${showPw ? 'text' : 'password'}" name="confirm" autocomplete="new-password"
                 placeholder="${escapeHtml(t('auth.passwordPlaceholder'))}" />
        </span>
      </label>

      ${errorMsg ? `<div class="auth-error">${icon('error', 16)}<span>${escapeHtml(errorMsg)}</span></div>` : ''}

      <button class="auth-submit" type="submit" ${busy ? 'disabled' : ''}>
        ${icon('person_add', 20)}
        <span>${escapeHtml(busy ? t('auth.pleaseWait') : t('invite.cta'))}</span>
      </button>
    </form>`);

  bindLangSelect(rootEl, draw);
  const form = rootEl.querySelector('#inv-form');
  form.addEventListener('submit', onSubmit);
  // Auge: beide Passwortfelder gemeinsam sichtbar/unsichtbar
  rootEl.querySelector('[data-eye]')?.addEventListener('click', () => { showPw = !showPw; draw(); });
  form.querySelector('input[name="name"]')?.focus();
}

async function onSubmit(e) {
  e.preventDefault();
  if (busy) return;

  const form = e.currentTarget;
  const name = (form.elements.namedItem('name')?.value || '').trim();
  const password = form.elements.namedItem('password')?.value || '';
  const confirm = form.elements.namedItem('confirm')?.value || '';

  if (password.length < 8) {
    errorMsg = t('auth.errors.passwordShort');
    draw();
    return;
  }
  if (password !== confirm) {
    errorMsg = t('invite.errors.mismatch');
    draw();
    return;
  }

  busy = true;
  errorMsg = '';
  draw();

  try {
    await acceptInvite(token, password, name || undefined);
    window.location.href = '/'; // وارد اپ
  } catch (err) {
    busy = false;
    if (err.status === 409) errorMsg = t('invite.errors.taken');
    else if (err.status === 400) errorMsg = t('invite.errors.expired');
    else errorMsg = err.message || t('auth.errors.generic');
    draw();
  }
}
